import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import client from '../../apollo/client';
import {
  GET_PENDING_COACH_REQUESTS,
  GET_APPROVED_COACHES
} from '../../apollo/queries/user';
import {
  APPROVE_COACH_REQUEST,
  REJECT_COACH_REQUEST,
  DELETE_COACH
} from '../../apollo/mutations/user';

export const fetchPendingCoaches = createAsyncThunk(
  'admin/fetchPendingCoaches',
  async (_, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: GET_PENDING_COACH_REQUESTS,
        fetchPolicy: 'no-cache'
      });
      return data.getPendingCoachRequests;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const fetchApprovedCoaches = createAsyncThunk(
  'admin/fetchApprovedCoaches',
  async (_, thunkAPI) => {
    try {
      const { data } = await client.query({
        query: GET_APPROVED_COACHES,
        fetchPolicy: 'no-cache'
      });
      return data.getApprovedCoaches;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const approveCoach = createAsyncThunk(
  'admin/approveCoach',
  async (requestId, thunkAPI) => {
    try {
      await client.mutate({
        mutation: APPROVE_COACH_REQUEST,
        variables: { requestId },
      });

      const coach = thunkAPI
        .getState()
        .admin.pendingCoaches.find((c) => c._id === requestId);

      return coach;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const rejectCoach = createAsyncThunk(
  'admin/rejectCoach',
  async (requestId, thunkAPI) => {
    try {
      await client.mutate({
        mutation: REJECT_COACH_REQUEST,
        variables: { requestId },
      });

      return requestId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

export const deleteCoach = createAsyncThunk(
  'admin/deleteCoach',
  async (coachId, thunkAPI) => {
    try {
      await client.mutate({
        mutation: DELETE_COACH,
        variables: { coachId },
      });
      return coachId;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    pendingCoaches: [],
    approvedCoaches: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearAdminState: (state) => {
      state.pendingCoaches = [];
      state.approvedCoaches = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Pending Coaches
      .addCase(fetchPendingCoaches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingCoaches.fulfilled, (state, action) => {
        state.pendingCoaches = action.payload;
        state.loading = false;
      })

      // Approved Coaches
      .addCase(fetchApprovedCoaches.fulfilled, (state, action) => {
        state.approvedCoaches = action.payload;
      })

      // Approve
      .addCase(approveCoach.fulfilled, (state, action) => {
        const approvedCoach = action.payload;
        state.pendingCoaches = state.pendingCoaches.filter(c => c._id !== approvedCoach._id);
        state.approvedCoaches.push(approvedCoach);
        state.loading = false;
        state.error = null;
      })

      // Reject
      .addCase(rejectCoach.fulfilled, (state, action) => {
        const rejectedId = action.payload;
        state.pendingCoaches = state.pendingCoaches.filter(c => c._id !== rejectedId);
        state.loading = false;
        state.error = null;
      })

      // Delete Coach
      .addCase(deleteCoach.fulfilled, (state, action) => {
        state.approvedCoaches = state.approvedCoaches.filter(c => c._id !== action.payload);
      })

      // Error Handling
      .addCase(approveCoach.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(rejectCoach.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCoach.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearAdminState } = adminSlice.actions;
export default adminSlice.reducer;